/* ══════════════════════════════════════════════════════════════════════════════
 * pixel-analytics — events custom du thème
 *
 * PÉRIMÈTRE : uniquement les events que l'app Stape Conversion Tracking ne
 * couvre pas. Stape charge GTM et pousse tout le funnel e-commerce standard via
 * ses events suffixés `_stape` :
 *
 *   page_view · view_collection · select_item · view_item · search_submitted
 *   add_to_cart · view_cart · remove_from_cart · begin_checkout
 *   add_contact_info · add_shipping_info · add_payment_info · purchase
 *
 * Avant d'ajouter un event, vérifier qu'il n'est pas dans cette liste : un
 * doublon fausserait les rapports. Ne jamais écrire de clé `ecommerce`, elle
 * appartient aux events `_stape` et le dataLayer est cumulatif.
 *
 * EXCEPTION — `view_cart`. La boutique est en `cart_type: "drawer"`, donc la
 * page /cart n'est jamais vue et l'event Shopify `cart_viewed` dont dérive
 * `view_cart_stape` ne se déclenche jamais. Le thème l'émet donc lui-même à
 * l'ouverture du tiroir, avec sa clé `ecommerce` (voir le bloc view_cart en
 * fin de fichier pour le détail du raisonnement et les garde-fous).
 *
 * CONVENTIONS
 *   - Accroche au DOM par attribut `data-gtm-*`, jamais par classe CSS du thème
 *     (une classe change à la première mise à jour Stretch, le tracking casse
 *     en silence).
 *   - Un bloc par event, ouvert par un commentaire au nom de l'event et fermé
 *     par un commentaire « Fin <nom> » (voir scroll_50 plus bas).
 *   - Fichier écrit à la main, pas de build : ce que tu lis ici est exactement
 *     ce qui tourne dans le navigateur.
 *
 * DEBUG : `?ax_debug=1` sur n'importe quelle URL logge chaque push.
 * ═════════════════════════════════════════════════════════════════════════════ */

(() => {
  window.dataLayer = window.dataLayer || [];

  const DEBUG = new URLSearchParams(window.location.search).has('ax_debug');

  /* ─── helpers ─────────────────────────────────────────────────────────────── */

  /** Seul point d'écriture dans le dataLayer. */
  const push = (payload) => {
    window.dataLayer.push(payload);

    if (DEBUG) {
      console.log('%c[gtm]%c ' + payload.event, 'color:#7c3aed;font-weight:700', '', payload);
    }
  };

  /**
   * Écoute déléguée sur un attribut, en phase de capture.
   *
   * Déléguer sur `document` couvre les éléments injectés après le chargement
   * (re-render de section, modale quick-buy, widget tiers) — ce qu'un
   * `querySelectorAll` au DOMContentLoaded rate.
   * La capture garantit qu'un `stopPropagation()` d'un script du thème ne peut
   * pas escamoter le tracking.
   */
  const onEvent = (type, attribute, handler) => {
    document.addEventListener(type, (event) => {
      const element = event.target.closest && event.target.closest('[' + attribute + ']');

      if (element) {
        handler(element, event);
      }
    }, true);
  };

  const onClick = (attribute, handler) => onEvent('click', attribute, handler);

  /** ID client Shopify, posé sur le <body> par layout/theme.liquid. Vide si anonyme. */
  const userId = () => document.body.dataset.gtmUserId || '';

  /** Devise de présentation du marché courant, posée sur le <body> par layout/theme.liquid. */
  const currency = () => document.body.dataset.gtmCurrency || '';

  /* ─── events ──────────────────────────────────────────────────────────────── */

  /* scroll_30 / scroll_50 / scroll_70 / scroll_80 / scroll
   *
   * Profondeur de scroll atteinte. Ajouter un seuil = ajouter une ligne dans
   * SCROLL_STEPS. Le nom n'est pas dérivé du seuil mais déclaré explicitement,
   * parce que le palier à 90% s'appelle `scroll` tout court — c'est le nom que
   * GA4 utilise nativement pour la profondeur de scroll (enhanced measurement).
   *
   * Mesure : (scrollY + innerHeight) / scrollHeight, soit le bas du viewport
   * rapporté à la hauteur du document — « le visiteur a vu N% de la page ».
   * L'autre convention, scrollY / (scrollHeight - innerHeight), déclenche plus
   * tard ; les deux coïncident à 100%.
   *
   * Chaque seuil ne part qu'une fois par chargement de page.
   */
  const SCROLL_STEPS = [
    [30, 'scroll_30'],
    [50, 'scroll_50'],
    [70, 'scroll_70'],
    [80, 'scroll_80'],
    [90, 'scroll']
  ];

  const scrollPending = new Set(SCROLL_STEPS.map(([threshold]) => threshold));
  let scrollScheduled = false;

  const measureScroll = () => {
    scrollScheduled = false;

    const documentHeight = document.documentElement.scrollHeight;
    const percent = documentHeight
      ? ((window.scrollY + window.innerHeight) / documentHeight) * 100
      : 0;

    SCROLL_STEPS.forEach(([threshold, name]) => {
      if (!scrollPending.has(threshold) || percent < threshold) {
        return;
      }

      scrollPending.delete(threshold);

      const payload = { event: name };
      const id = userId();

      if (id) {
        payload.user_id = id;
      }

      push(payload);
    });

    if (scrollPending.size === 0) {
      window.removeEventListener('scroll', scheduleScroll);
      window.removeEventListener('resize', scheduleScroll);
    }
  };

  // Le scroll émet des dizaines d'events par seconde : une seule mesure par frame.
  const scheduleScroll = () => {
    if (scrollScheduled) {
      return;
    }

    scrollScheduled = true;
    window.requestAnimationFrame(measureScroll);
  };

  window.addEventListener('scroll', scheduleScroll, { passive: true });
  window.addEventListener('resize', scheduleScroll, { passive: true });

  // Page plus courte que le viewport : les seuils sont atteints sans scroller.
  // On attend `load`, sinon les images pas encore dimensionnées donnent une
  // hauteur de document trop faible et un faux positif.
  if (document.readyState === 'complete') {
    scheduleScroll();
  } else {
    window.addEventListener('load', scheduleScroll, { once: true });
  }
  /* Fin scroll_30 / scroll_50 / scroll_70 / scroll_80 / scroll */

  /* clic_menu
   *
   * Clic sur un lien du menu, tous niveaux et tous rendus confondus.
   *
   * L'accroche `data-gtm-menu` est posée sur les CONTENEURS de menu, pas sur
   * chaque lien : 6 attributs au lieu d'une vingtaine, et tout lien ajouté plus
   * tard dans ces conteneurs est couvert sans retoucher au JS.
   *   - sections/header.liquid ............ nav primaire desktop (N1/N2/N3 + mega menu)
   *   - main-menu-drawer.liquid ........... N1 mobile, menu secondaire, panneaux N2/N3
   *   - main-menu-dropdown-sidebar.liquid . N1 sidebar, panneaux N2/N3 + secondaire
   *
   * Les conteneurs ont été choisis pour n'englober QUE du menu : les liens
   * réseaux sociaux (render 'social-media') et les sélecteurs de langue/pays
   * restent en dehors, donc pas de faux positif.
   *
   * Le libellé est lu dans le lien : les <a> du menu ne contiennent que
   * {{ link.title }}, sans icône ni span sr-only, donc textContent est propre.
   *
   * À noter : en mobile, les entrées de niveau 1 qui déplient un sous-menu sont
   * des <button data-panel-toggle>, pas des <a> — elles ne déclenchent donc rien.
   * En desktop ce sont de vrais <a href> : un clic sur un parent pousse l'event
   * même si le thème ouvre le sous-menu au lieu de naviguer.
   */
  onClick('data-gtm-menu', (menu, event) => {
    const link = event.target.closest('a[href]');

    // Clic dans le menu mais pas sur un lien (bouton de dépliage, zone vide).
    if (!link || !menu.contains(link)) {
      return;
    }

    const payload = {
      event: 'clic_menu',
      menu_lien_nom: link.textContent.replace(/\s+/g, ' ').trim(),
      menu_lien_url: link.href
    };

    const id = userId();

    if (id) {
      payload.user_id = id;
    }

    push(payload);
  });
  /* Fin clic_menu */

  /* click_newsletter
   *
   * Inscription newsletter RÉUSSIE (et non simple soumission).
   *
   * Le formulaire se soumet en natif, sans AJAX (rien dans js/common/form/) :
   * la page recharge et Shopify rend la branche `form.posted_successfully?`.
   * On ne peut donc pas mesurer la réussite sur un `submit` — on la lit au
   * chargement suivant, sur un marqueur que le Liquid ne rend QUE dans cette
   * branche :
   *
   *   <div hidden data-gtm-newsletter-success></div>
   *
   * Posé dans les 5 formulaires newsletter du thème :
   *   footer.liquid · newsletter.liquid · main-blog.liquid
   *   newsletter-popup.liquid · main-password.liquid
   *
   * Avantage sur la version submit : une adresse refusée ou déjà inscrite ne
   * compte plus, et le push ne risque plus d'être perdu dans le rechargement.
   *
   * `button_id` = l'id du FORMULAIRE, lu sur le <form> parent du marqueur.
   * Aucune valeur n'est recopiée dans le Liquid : l'id suit automatiquement
   * celui que Shopify rend, y compris sur main-password.liquid dont le
   * {% form %} n'a pas de paramètre `id` (Shopify y met 'contact_form').
   */
  const newsletterSuccess = document.querySelectorAll('[data-gtm-newsletter-success]');

  if (newsletterSuccess.length > 0) {
    // Un seul push, même si Shopify marquait plusieurs formulaires de la page
    // comme réussis (le footer et le popup coexistent sur toutes les pages).
    // Mieux vaut sous-compter que gonfler les conversions.
    if (newsletterSuccess.length > 1 && DEBUG) {
      console.log('%c[gtm]%c ' + newsletterSuccess.length
        + ' formulaires newsletter en succès, 1 seul event poussé', 'color:#7c3aed;font-weight:700', '');
    }

    const newsletterForm = newsletterSuccess[0].closest('form');
    const payload = { event: 'click_newsletter' };

    if (newsletterForm && newsletterForm.id) {
      payload.button_id = newsletterForm.id;
    }

    push(payload);
  }
  /* Fin click_newsletter */

  /* info
   *
   * Déroulement d'une info dépliable en fiche produit (description, entretien,
   * responsabilité, bloc accordéon libre).
   *
   * OUVERTURE SEULEMENT. Le composant <accordion-disclosure> appelle
   * event.preventDefault() sur le clic du <summary> et pilote lui-même
   * `details.open` avec une animation (js/common/navigation/accordion-disclosure.js).
   * Notre écoute est en phase de capture sur document, donc elle passe AVANT ce
   * handler : à cet instant `open` reflète encore l'état d'AVANT la bascule.
   *   open === false  → l'accordéon va s'ouvrir      → on pousse
   *   open === true   → il va se fermer              → on ignore
   * Ce test filtre aussi les clics sur un lien ou un bouton dans le contenu
   * d'un accordéon déjà ouvert.
   *
   * `data-gtm-info` est émis par snippets/accordion.liquid, uniquement quand
   * l'appelant passe `gtm_info_id`. Cet opt-in est nécessaire parce que le
   * composant est partagé avec le footer, la FAQ, le panier et les facettes —
   * qui ne doivent pas remonter d'event. Les 6 appels tagués sont ceux de la
   * fiche produit (product-info.liquid ×4, product-care, product-responsibility).
   *
   * click_id    = data-block-id   (block.id Shopify)
   * click_label = data-block-name (le titre affiché de l'accordéon)
   */
  onClick('data-gtm-info', (info) => {
    if (info.open) {
      return;
    }

    const payload = { event: 'info' };

    if (info.dataset.blockId) {
      payload.click_id = info.dataset.blockId;
    }

    if (info.dataset.blockName) {
      payload.click_label = info.dataset.blockName;
    }

    push(payload);
  });
  /* Fin info */

  /* language
   *
   * Sélection d'une langue dans le sélecteur de localisation.
   *
   * `data-gtm-language` est posé sur chaque option de langue dans
   * snippets/localization-selector.liquid, branche `locale`. Une seule édition
   * couvre les 3 emplacements qui rendent ce snippet : footer.liquid,
   * header.liquid (nav secondaire) et main-menu-drawer.liquid.
   *
   * La branche `country` du même snippet n'est PAS taguée : ses options portent
   * name="country_code" et relèvent d'un autre event.
   *
   * `selected_language` = le code ISO (fr, en, de…) plutôt que l'endonyme
   * affiché (« Français », « English »). Le code est stable et indépendant de
   * la langue d'affichage ; l'endonyme, lui, change de casse et d'accents selon
   * la locale courante. Pour remonter le libellé à la place, il est dans le
   * <span> de l'option : option.textContent.trim().
   *
   * On écoute le clic et non le submit : les options sont des
   * <button type="submit"> et `event.submitter` — seul moyen de savoir laquelle
   * a été cliquée depuis un handler de submit — n'existe qu'à partir de
   * Safari 15.4, en dessous de la cible du thème (safari14.1).
   *
   * ATTENTION : sélectionner une langue soumet le {% form 'localization' %} et
   * la page RECHARGE juste après ce push. GTM envoie ses hits en sendBeacon
   * dans ce cas, ce qui tient dans la grande majorité des cas.
   *
   * Un clic sur la langue DÉJÀ active pousse aussi l'event : c'est une
   * sélection, même si elle ne change rien. Pour l'exclure, tester
   * option.getAttribute('aria-selected') !== 'true'.
   */
  onClick('data-gtm-language', (option) => {
    push({ event: 'language', selected_language: option.dataset.gtmLanguage });
  });
  /* Fin language */

  /* filter
   *
   * Application de filtres sur une page collection ou de recherche.
   *
   * DÉCLENCHEMENT : l'event `facet:update` du thème, émis au moment où les
   * filtres sont réellement appliqués. En disposition drawer (réglage actuel,
   * update_on_change: false) c'est le clic sur « Appliquer » ; en sidebar
   * (update_on_change: true) c'est chaque changement. Un filtre coché puis
   * décoché avant validation ne remonte donc rien.
   *
   * Le TRI compte comme un filtre : il figure dans `filters` sous son libellé
   * (« Trier par ») et entre dans la signature, donc changer le tri rejoue
   * l'event. Deux markups selon la disposition — les radios name="sort_by" du
   * drawer (facets.liquid:53) ou les liens du <sort-by-popover> en sidebar
   * (collection-faceting-button.liquid) — les deux sont résolus.
   *
   * `facet:update` est en revanche aussi émis par la PAGINATION
   * (pagination.liquid), qui ne doit rien remonter. Comparer la signature des
   * paramètres `filter.*` + `sort_by` écarte ce cas sans avoir à inspecter la
   * provenance de l'event.
   *
   * LIBELLÉS : jamais lus dans la valeur brute. Pour un filtre basé sur une
   * metafield reference Shopify, `value` contient un GID (gid://shopify/…)
   * inutilisable en rapport. On résout donc chaque valeur vers son libellé
   * affiché, via le <label> de l'input — les swatches et les filtres image y
   * mettent leur libellé dans un <span class="sr-only"> ou .image-filter__name,
   * que textContent inclut.
   *
   * filter_name = le dernier filtre modifié. Au retrait d'une puce
   * (active-facets.liquid) comme au changement de tri via le popover, aucun
   * `change` n'a lieu : on le déduit alors de la paire filtre/valeur qui
   * diffère de l'état précédent.
   */
  const FILTER_PARAM = 'filter.';
  const FILTER_PRICE_MIN = 'filter.v.price.gte';
  const FILTER_PRICE_MAX = 'filter.v.price.lte';
  const FILTER_SORT = 'sort_by';

  const isFilterParam = (name) => name.indexOf(FILTER_PARAM) === 0 || name === FILTER_SORT;

  const filterText = (element) => element.textContent.replace(/\s+/g, ' ').trim();

  // Le compteur « (12) » est ajouté par facets.liquid quand le réglage
  // show_filter_values_count est actif : il ne fait pas partie du libellé.
  const filterOptionLabel = (input) => {
    const label = input.labels && input.labels[0];

    return label ? filterText(label).replace(/\s*\(\d+\)$/, '') : '';
  };

  // Le filtre de disponibilité est rendu hors accordéon (facets.liquid:69) :
  // on retombe alors sur le libellé de l'input lui-même.
  const filterGroupLabel = (input) => {
    const accordion = input.closest('accordion-disclosure');
    const summary = accordion && accordion.querySelector('summary');

    return summary ? filterText(summary) : filterOptionLabel(input);
  };

  // Index name+value -> input. Une Map plutôt qu'un sélecteur d'attribut : une
  // valeur de filtre peut contenir un guillemet (une taille 30") ou un GID.
  // Les inputs cachés sont exclus : facets.liquid:30 en pose un pour conserver
  // le tri courant, et il ne porte aucun libellé.
  const FILTER_INPUTS = '[data-gtm-filters] input[name^="filter."]:not([type="hidden"]),'
    + ' [data-gtm-filters] input[name="sort_by"]:not([type="hidden"])';

  const filterInputIndex = () => {
    const index = new Map();

    document.querySelectorAll(FILTER_INPUTS).forEach((input) => {
      index.set(input.name + '\n' + input.value, input);

      if (!index.has(input.name)) {
        index.set(input.name, input);
      }
    });

    return index;
  };

  // En sidebar, le tri est un jeu de liens dans <sort-by-popover> et non des
  // radios : on retrouve l'option par le sort_by de son href, et le libellé du
  // groupe est porté par data-gtm-sort sur le popover.
  const sortEntryFromPopover = (value) => {
    const popover = document.querySelector('[data-gtm-sort]');

    if (!popover) {
      return null;
    }

    const links = popover.querySelectorAll('a[href]');

    for (let i = 0; i < links.length; i += 1) {
      if (new URL(links[i].href, window.location.origin).searchParams.get(FILTER_SORT) === value) {
        return { filter: popover.getAttribute('data-gtm-sort'), value: filterText(links[i]) };
      }
    }

    return null;
  };

  const filterSignature = (params) => {
    const applied = [];

    params.forEach((value, name) => {
      if (isFilterParam(name)) {
        applied.push(name + '=' + value);
      }
    });

    return applied.sort().join('&');
  };

  const appliedFilters = (params) => {
    const index = filterInputIndex();
    const entries = [];
    let priceLabel = '';
    let priceMin = '';
    let priceMax = '';

    params.forEach((value, name) => {
      if (!isFilterParam(name)) {
        return;
      }

      if (name === FILTER_SORT) {
        const input = index.get(name + '\n' + value);

        if (input) {
          entries.push({ filter: filterGroupLabel(input), value: filterOptionLabel(input) });
        } else {
          entries.push(sortEntryFromPopover(value) || { filter: name, value: value });
        }

        return;
      }

      // Les deux bornes de prix sont fusionnées en une seule entrée. Leurs
      // <label> ne contiennent que le symbole monétaire, donc la valeur
      // numérique de l'URL fait office de libellé.
      if (name === FILTER_PRICE_MIN || name === FILTER_PRICE_MAX) {
        const priceInput = index.get(name);

        if (priceInput && !priceLabel) {
          priceLabel = filterGroupLabel(priceInput);
        }

        if (name === FILTER_PRICE_MIN) {
          priceMin = value;
        } else {
          priceMax = value;
        }

        return;
      }

      const input = index.get(name + '\n' + value);

      entries.push({
        filter: input ? filterGroupLabel(input) : name,
        value: input ? filterOptionLabel(input) : value
      });
    });

    if (priceMin || priceMax) {
      entries.push({ filter: priceLabel, value: (priceMin + ' - ' + priceMax).trim() });
    }

    return entries;
  };

  let filterLastName = '';
  let filterLastSignature = '';
  let filterLastApplied = [];

  if (document.querySelector('[data-gtm-filters]')) {
    const initialParams = new URLSearchParams(window.location.search);

    filterLastSignature = filterSignature(initialParams);
    filterLastApplied = appliedFilters(initialParams);
  }

  onEvent('change', 'data-gtm-filters', (form, event) => {
    const input = event.target;

    if (input && input.name && isFilterParam(input.name)) {
      filterLastName = filterGroupLabel(input);
    }
  });

  document.addEventListener('facet:update', (event) => {
    const url = event.detail && event.detail.url;

    if (!url) {
      return;
    }

    const signature = filterSignature(url.searchParams);

    if (signature === filterLastSignature) {
      return; // pagination, tri, ou ré-application à l'identique
    }

    const filters = appliedFilters(url.searchParams);
    let name = filterLastName;

    if (!name) {
      // Comparer les paires filtre/valeur et non les seuls groupes : un
      // changement de tri garde le groupe « Trier par » et ne change que sa
      // valeur, un diff par groupe ne le verrait pas.
      const pair = (entry) => entry.filter + '\n' + entry.value;
      const before = filterLastApplied.map(pair);
      const after = filters.map(pair);
      const changed = filterLastApplied.filter((entry) => after.indexOf(pair(entry)) === -1)[0]
        || filters.filter((entry) => before.indexOf(pair(entry)) === -1)[0];

      name = changed ? changed.filter : '';
    }

    push({ event: 'filter', filter_name: name, filters: filters });

    filterLastSignature = signature;
    filterLastApplied = filters;
    filterLastName = '';
  }, true);
  /* Fin filter */

  /* clic_blancsurmesure
   *
   * Clic sur un lien vers le site partenaire Le Blanc sur Mesure.
   *
   * PAS de data-gtm ici, et c'est volontaire. Le lien vit dans du contenu
   * marchand — la valeur du setting `answer` d'un bloc question
   * (templates/page.cms-faq.json), donc du HTML saisi dans l'éditeur de thème.
   * Aucun Liquid à taguer, et un attribut injecté dans ce JSON serait effacé à
   * la première réédition de la réponse par un contributeur.
   *
   * L'accroche est donc le href lui-même, ce qui est ici PLUS pérenne qu'un
   * attribut : c'est la destination du lien, c'est-à-dire l'objet même de
   * l'event. Ça survit aux réécritures du texte et ça couvre sans rien changer
   * les autres endroits où le lien pourrait apparaître plus tard — une autre
   * question de la FAQ, une page, une entrée de menu, le footer.
   *
   * Le test porte sur le hostname et non sur la chaîne du href : il couvre donc
   * http/https, avec ou sans www, avec un chemin ou des paramètres UTM.
   */
  const BLANC_SUR_MESURE_HOST = 'leblancsurmesure.com';

  onClick('href', (link) => {
    const host = link.hostname || '';

    if (host === BLANC_SUR_MESURE_HOST || host.slice(-(BLANC_SUR_MESURE_HOST.length + 1)) === '.' + BLANC_SUR_MESURE_HOST) {
      push({ event: 'clic_blancsurmesure' });
    }
  });
  /* Fin clic_blancsurmesure */

  /* clic_pagePro
   *
   * Clic sur un lien vers le site professionnel.
   *
   * Même situation que clic_blancsurmesure : le lien vit dans du contenu
   * marchand (réponse d'un bloc question de templates/page.cms-faq.json), donc
   * aucun Liquid à taguer et l'accroche est le href.
   *
   * Mais le test est ici sur l'hôte EXACT, pas sur le suffixe :
   * traditiondesvosges.com est le domaine du site lui-même, et il apparaît dans
   * des dizaines de liens internes du contenu marchand. Un test par suffixe
   * ferait partir clic_pagePro sur toute la navigation interne.
   */
  const PAGE_PRO_HOST = 'pro.traditiondesvosges.com';

  onClick('href', (link) => {
    if (link.hostname === PAGE_PRO_HOST) {
      push({ event: 'clic_pagePro' });
    }
  });
  /* Fin clic_pagePro */

  /* click_guide_des_tailles
   *
   * Ouverture du guide des tailles depuis le sélecteur de variante
   * (snippets/variant-picker.liquid). Le bouton n'est rendu que si le produit a
   * une option reconnue comme une taille (clé general.label.size, « Taille » en
   * français) ET que le metafield custom.guide_des_tailles est renseigné.
   *
   * L'objet `item` reprend le schéma de l'app Stape (view_item_stape) plutôt
   * que le GA4 canonique, pour que les deux events soient superposables dans
   * GTM et partagent les mêmes variables. Trois écarts avec GA4 :
   *   item_id      = id PRODUIT (et non de variante)
   *   item_variant = id de VARIANTE (et non son libellé)
   *   variant_name = le libellé de variante, « Default Title » compris
   * Stape envoie aussi item_sku, imageURL et item_url, et sérialise `value` et
   * `quantity` en CHAÎNES alors que `price` reste un nombre : on fait pareil.
   *
   * `currency`, `value` et `item` sont à la racine du push, pas sous une clé
   * `ecommerce` — celle-ci appartient aux events `_stape`.
   *
   * Le prix suit la variante SÉLECTIONNÉE : le thème émet `variant:change` avec
   * {formId, variant} depuis le formulaire produit (variant-picker.js:114). On
   * mémorise la dernière variante par formulaire, ce qui reste juste quand une
   * page porte plusieurs produits (fiche + produit mis en avant), et sans
   * dépendre d'un re-rendu du bloc guide des tailles.
   */
  const sizeGuideVariants = new Map();

  document.addEventListener('variant:change', (event) => {
    const detail = event.detail;

    if (detail && detail.formId && detail.variant) {
      sizeGuideVariants.set(detail.formId, detail.variant);
    }
  }, true);

  onClick('data-gtm-size-guide', (button) => {
    const data = button.dataset;
    const form = button.closest('form');
    const variant = form ? sizeGuideVariants.get(form.id) : null;

    // Shopify exprime les prix en centimes entiers, GA4 attend un décimal.
    const price = variant ? Math.round(variant.price) / 100 : parseFloat(data.gtmPrice);
    const hasPrice = !isNaN(price);

    // Ordre des clés aligné sur celui de Stape, pour une lecture côté DevTools
    // directement comparable.
    const item = { item_id: data.gtmItemId };

    const sku = variant ? variant.sku : data.gtmItemSku;

    if (sku) {
      item.item_sku = sku;
    }

    item.item_variant = String(variant ? variant.id : data.gtmItemVariant);
    item.item_name = data.gtmItemName;
    item.variant_name = variant ? variant.title : data.gtmVariantName;

    if (data.gtmItemCategory) {
      item.item_category = data.gtmItemCategory;
    }

    if (hasPrice) {
      item.price = price;
    }

    if (data.gtmItemBrand) {
      item.item_brand = data.gtmItemBrand;
    }

    if (data.gtmImageUrl) {
      item.imageURL = data.gtmImageUrl;
    }

    if (data.gtmItemUrl) {
      item.item_url = data.gtmItemUrl;
    }

    item.quantity = '1';

    push({
      event: 'click_guide_des_tailles',
      currency: data.gtmCurrency,
      value: hasPrice ? String(price) : '0',
      item: item
    });
  });
  /* Fin click_guide_des_tailles */

  /* store_search / store_detail / store_get_direction / store_click_to_call
   *
   * Page magasins. Le store locator est l'app UBERALL, qui remplace Progus
   * depuis le 2026-09. Différence décisive : Uberall rend son widget DANS le
   * DOM de la page (#store-finder-widget), alors que Progus l'enfermait dans
   * une iframe cross-origin. On peut donc écouter les clics normalement, au
   * lieu de passer par du postMessage.
   *
   * Pas de data-gtm-* pour autant : le markup est produit par le script
   * d'Uberall, on ne peut rien y poser depuis le thème. On s'accroche donc à
   * ce qu'Uberall expose de plus stable, et jamais à une de ses classes CSS :
   *   [data-location-id] / [data-location-identifier] .... ses attributs de données
   *   [data-testid="autocomplete-mapbox"] ................ son hook de test du formulaire
   *   le href des liens ................................... tel:, /maps/dir, #!/l/
   *
   * Tout est scopé à #store-finder-widget : sans ça, n'importe quel lien tel:
   * du site déclencherait store_click_to_call.
   *
   * Les trois paramètres magasin sont lus sur le <li> de la fiche :
   *   store_id   = data-location-identifier (« TDV007 »), le code magasin
   *                métier. Uberall porte aussi data-location-id (« 4712899 »),
   *                son id interne, si tu préfères celui-là.
   *   store_name = le libellé du lien de détail
   *   store_city = le segment ville de l'URL de détail (#!/l/<ville>/<rue>/<code>),
   *                donc « gérardmer ». La forme affichée « 88400 Gérardmer »
   *                n'existe que dans un élément à classe CSS : on ne s'y accroche pas.
   *
   * Le terme de recherche n'existe qu'à la soumission : on le conserve pour le
   * rattacher aux trois autres events, comme le faisait le pont Progus.
   */
  const STORE_WIDGET = '#store-finder-widget';
  const STORE_ITEM = '[data-location-id]';
  const STORE_DETAIL_ROUTE = '#!/l/';

  let storeSearchTerm = '';

  const storePush = (name, element) => {
    const payload = { event: name };
    const item = element.closest(STORE_ITEM);

    if (item) {
      const identifier = item.getAttribute('data-location-identifier');

      if (identifier) {
        payload.store_id = identifier;
      }

      const detail = item.querySelector('a[href*="' + STORE_DETAIL_ROUTE + '"]');

      if (detail) {
        const storeName = detail.textContent.replace(/\s+/g, ' ').trim();

        if (storeName) {
          payload.store_name = storeName;
        }

        const route = (detail.getAttribute('href') || '').split(STORE_DETAIL_ROUTE)[1] || '';
        const city = decodeURIComponent(route).split('/')[0];

        if (city) {
          payload.store_city = city;
        }
      }
    }

    if (storeSearchTerm) {
      payload.store_region_search_term = storeSearchTerm;
    }

    push(payload);
  };

  onEvent('submit', 'data-testid="autocomplete-mapbox"', (form) => {
    const input = form.querySelector('input[type="text"], input[type="search"]');

    storeSearchTerm = input ? input.value.replace(/\s+/g, ' ').trim() : '';

    const payload = { event: 'store_search' };

    if (storeSearchTerm) {
      payload.store_region_search_term = storeSearchTerm;
    }

    // Aucun magasin n'est encore choisi à ce stade : pas de store_id/name/city.
    push(payload);
  });

  onClick('href', (link) => {
    if (!link.closest(STORE_WIDGET)) {
      return;
    }

    const href = link.getAttribute('href') || '';

    if (href.slice(0, 4) === 'tel:') {
      storePush('store_click_to_call', link);
    } else if (href.indexOf('/maps/dir') !== -1) {
      storePush('store_get_direction', link);
    } else if (href.indexOf(STORE_DETAIL_ROUTE) !== -1) {
      storePush('store_detail', link);
    }
  });
  /* Fin store_search / store_detail / store_get_direction / store_click_to_call */

  /* chat_opened / chat_message_sent / chat_closed
   *
   * Chatbot Crisp (app embed crisp-live-chat-chatbot).
   *
   * Aucun sélecteur DOM ici, et c'est heureux : Crisp rend sa chatbox avec des
   * classes obfusquées (.cc-13wro…) qui changent à chaque build. Il expose en
   * revanche une API d'events officielle, qui est le point d'accroche le plus
   * stable possible — c'est un contrat public, pas du markup.
   *
   * `window.$crisp` est une simple file d'attente tant que le SDK n'est pas
   * chargé, puis l'objet réel qui rejoue la file. Le `|| []` couvre donc les
   * deux cas, quel que soit l'ordre de chargement entre ce fichier et l'app
   * embed Crisp. C'est le motif documenté par Crisp.
   *
   * Le callback de `message:sent` reçoit le message en premier argument. On ne
   * le remonte PAS : le contenu d'une conversation est de la donnée
   * personnelle, elle n'a rien à faire dans le dataLayer ni dans GA4. Le plan
   * de marquage ne prévoit d'ailleurs aucun paramètre sur ces trois events.
   *
   * À noter : iAdvize est aussi chargé sur le site mais ne rend aucun widget
   * visible. Si le chat venait à basculer vers lui, ce bloc ne couvrirait rien.
   *
   * ATTENTION à `message:sent` : contrairement à chat:opened et chat:closed,
   * qui sont purement client, Crisp ne l'émet qu'à l'ACCUSÉ DE RÉCEPTION du
   * serveur (client_default.js : `this.Vl.on("message:sent", … Action.event(…))`
   * déclenché par handleSentAcknowledgement). Il lui faut donc une session
   * ouverte et un aller-retour réseau réussi. Support hors ligne, socket
   * bloqué par une CMP ou un bloqueur : le message part visuellement mais
   * l'event ne se déclenche jamais. Si chat_message_sent semble manquer alors
   * que chat_opened fonctionne, chercher de ce côté avant de suspecter le nom
   * de l'event — vérifié conforme à la doc Crisp et à la liste blanche du SDK.
   */
  window.$crisp = window.$crisp || [];

  window.$crisp.push(['on', 'chat:opened', () => push({ event: 'chat_opened' })]);
  window.$crisp.push(['on', 'message:sent', () => push({ event: 'chat_message_sent' })]);
  window.$crisp.push(['on', 'chat:closed', () => push({ event: 'chat_closed' })]);
  /* Fin chat_opened / chat_message_sent / chat_closed */

  /* add_to_wishlist
   *
   * Ajout d'un produit aux favoris (app Wishlist Power). Le bouton est présent
   * sur la cover de la fiche produit (product-gallery.liquid) et sur chaque
   * carte produit (product-card.liquid) : les deux portent data-gtm-wishlist.
   *
   * Le bouton est un TOGGLE : il faut distinguer l'ajout du retrait. L'app
   * matérialise l'état par `aria-checked` sur le <button> interne — un attribut
   * ARIA standard, pas une classe.
   *
   * On observe la TRANSITION de cet attribut plutôt que le clic, et c'est
   * délibéré. Vérifié en direct sur le site : à l'instant du clic, même en
   * phase de capture sur document, `aria-checked` vaut DÉJÀ la valeur d'après
   * bascule — l'app change l'état avant l'event click, vraisemblablement sur
   * mousedown. Un listener de clic marcherait donc, mais à l'envers de ce que
   * l'intuition dicte, et s'inverserait silencieusement si l'app basculait un
   * jour sur `click`. Observer la transition ne dépend d'aucun de ces choix.
   *
   * L'event compte un CHANGEMENT D'ÉTAT, pas une mutation d'attribut : un même
   * ajout peut en produire plusieurs. On tient donc un registre des produits
   * déjà en favoris et on ne pousse qu'à l'entrée dans ce registre.
   *
   * L'observation couvre aussi les boutons injectés après coup (filtres de
   * collection, quick-buy), que le thème re-rend.
   *
   * Paramètres : `currency` et `user_id`, tous deux lus sur le <body>
   * (layout/theme.liquid). Ce sont des constantes de page, pas des données du
   * bouton — inutile de les répéter sur chaque cœur. Chacun est omis s'il est
   * vide, `user_id` l'étant pour un visiteur anonyme.
   *
   * À noter que l'élément porte aussi product-id et handle, écrits par notre
   * Liquid, si le besoin d'identifier le produit apparaît.
   */
  // Produits qu'on considère déjà en favoris. C'est ce registre, et non la
  // mutation elle-même, qui décide s'il y a event : un même ajout peut produire
  // plusieurs mutations (pose optimiste puis confirmation serveur, re-sets
  // redondants observés en direct : false->true suivi de true->true), et l'app
  // synchronise l'état de TOUS les boutons d'un même produit quand la page en
  // affiche plusieurs. Dédupliquer par product-id rend le compte exact quelle
  // que soit la séquence.
  const wishlistAdded = new Set();

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const button = mutation.target;
      const wrapper = button.closest('[data-gtm-wishlist]');

      if (!wrapper) {
        return;
      }

      const productId = wrapper.getAttribute('product-id') || '';

      if (button.getAttribute('aria-checked') !== 'true') {
        wishlistAdded.delete(productId);

        return;
      }

      if (wishlistAdded.has(productId)) {
        return;
      }

      wishlistAdded.add(productId);

      // Pose initiale de l'attribut au chargement : le produit était déjà en
      // favoris, on mémorise son état sans compter d'ajout.
      if (mutation.oldValue === null) {
        return;
      }

      const payload = { event: 'add_to_wishlist' };
      const storeCurrency = currency();
      const id = userId();

      if (storeCurrency) {
        payload.currency = storeCurrency;
      }

      if (id) {
        payload.user_id = id;
      }

      push(payload);
    });
  }).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-checked'],
    attributeOldValue: true
  });
  /* Fin add_to_wishlist */

  /* view_cart
   *
   * `cart_type` vaut "drawer" (config/settings_data.json) : le parcours normal
   * n'affiche jamais /cart. Or `view_cart_stape` dérive de l'event standard
   * Shopify `cart_viewed`, qui ne se déclenche QUE sur une vue de la page
   * panier. L'event n'était donc jamais émis — rien à écouter côté GTM.
   *
   * On l'émet ici à chaque ouverture du tiroir.
   *
   * Exception assumée à la règle « ne jamais écrire de clé `ecommerce` » de
   * l'en-tête : cette règle protège d'une collision avec les events `_stape`,
   * or ceux-ci vivent dans le dataLayer de la sandbox du Web Pixel, pas dans
   * celui de la page. Aucun `ecommerce` n'existe ici. Le `ecommerce: null`
   * poussé juste avant garantit en plus qu'aucune clé d'un push précédent ne
   * fuite dans celui-ci (le dataLayer est cumulatif).
   *
   * `dialog:after-show` ne bulle pas : on écoute en phase de capture sur
   * `document`, qui traverse quand même l'arbre à la descente. Ça survit au
   * re-render de la section panier, qui remplace l'élément <cart-drawer>.
   *
   * Source des données : /cart.js plutôt que le DOM du tiroir — le balisage
   * change à la première mise à jour Stretch, l'API non.
   */
  const CART_DRAWER_ID = 'cart-drawer';

  document.addEventListener('dialog:after-show', (event) => {
    const drawer = event.target;

    if (!drawer || drawer.id !== CART_DRAWER_ID) {
      return;
    }

    // Sur /cart, Stape émet déjà `view_cart_stape` via `cart_viewed` : ouvrir
    // le tiroir depuis cette page compterait l'event deux fois.
    if (/\/cart(\/|$)/.test(window.location.pathname)) {
      return;
    }

    fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart || !cart.item_count) {
          return;
        }

        const payload = {
          event: 'view_cart',
          ecommerce: {
            currency: cart.currency || currency(),
            // Shopify renvoie les montants en centimes.
            value: cart.total_price / 100,
            items: cart.items.map((item, index) => ({
              item_id: String(item.variant_id),
              item_name: item.product_title,
              item_brand: item.vendor || undefined,
              item_variant: item.variant_title || undefined,
              item_sku: item.sku || undefined,
              price: item.final_price / 100,
              quantity: item.quantity,
              index: index
            }))
          }
        };

        const id = userId();

        if (id) {
          payload.user_id = id;
        }

        // Purge des clés du push précédent avant d'écrire un nouvel `ecommerce`.
        push({ ecommerce: null });
        push(payload);
      })
      .catch(() => {
        // Panier injoignable : on n'émet pas d'event plutôt qu'un event faux.
      });
  }, true);
  /* Fin view_cart */

  if (DEBUG) {
    console.log('%c[gtm]%c pixel-analytics prêt', 'color:#7c3aed;font-weight:700', '');
  }
})();
